/** status.p1plug.nl: public, no sign-in, one page. */
import {
  SERVICE_STATE,
  createGateways,
  formatDate,
  formatDateTime,
  formatTime,
  type ServiceComponent,
  type ServiceState,
  type ServiceStatus,
} from '@p1plug/platform';
import {
  EmptyState,
  Notice,
  ResourceView,
  Section,
  ServiceStateLabel,
  StatusMark,
  Timeline,
  TopShell,
  useNow,
  useResource,
} from '@p1plug/ui';

const gateways = createGateways();

const RANK: ServiceState[] = ['major_outage', 'partial_outage', 'degraded', 'maintenance', 'operational'];

/** The headline: the worst monitored state, and never "all operational" while parts are unmeasured. */
function overall(s: ServiceStatus): { state: ServiceState; headline: string; detail: string } {
  const monitored = s.components.filter((c) => c.state !== 'not_monitored');
  const unmonitored = s.components.length - monitored.length;
  if (monitored.length === 0)
    return {
      state: 'not_monitored',
      headline: 'Status is not being measured yet',
      detail: 'No component has a measurement behind it.',
    };
  const worst = RANK.find((r) => monitored.some((c) => c.state === r)) ?? 'operational';
  const affected = monitored.filter((c) => c.state === worst).map((c) => c.name);
  if (worst !== 'operational')
    return {
      state: worst,
      headline: `${SERVICE_STATE[worst].label}: ${affected.join(', ')}`,
      detail: 'Details are in the incident below.',
    };
  return unmonitored === 0
    ? { state: 'operational', headline: 'All systems operational', detail: 'Every component is working as intended.' }
    : {
        state: 'operational',
        headline: 'Monitored systems are operational',
        detail: `${monitored.length} of ${s.components.length} components are measured. The others are marked below and say nothing about the service yet.`,
      };
}

function History({ c }: { c: ServiceComponent }) {
  if (c.history.length === 0) return null;
  const measured = c.history.filter((d): d is number => d !== null);
  const average = measured.reduce((a, b) => a + b, 0) / Math.max(measured.length, 1);
  return (
    <div className="st-history">
      <div
        className="st-days"
        role="img"
        aria-label={`${c.name}: ${average.toFixed(2)} % available over the last ${c.history.length} days`}
      >
        {c.history.map((d, i) => (
          <span
            key={i}
            className={d === null ? 'is-none' : d >= 99.9 ? 'is-full' : d >= 99 ? 'is-dip' : 'is-low'}
            title={d === null ? 'No measurement' : `${d.toFixed(2)} %`}
          />
        ))}
      </div>
      <div className="st-history-scale">
        <span>{c.history.length} days ago</span>
        <span className="p1-num">{average.toFixed(2)} % available</span>
        <span>Today</span>
      </div>
    </div>
  );
}

export function App() {
  const now = useNow(30_000);
  const status = useResource(() => gateways.status.status(), [], { refreshMs: 60_000 });
  return (
    <TopShell
      product="Status"
      width="narrow"
      nav={[]}
      demo={gateways.mode === 'demo'}
      demoText="Demo. Components, incidents and availability on this page are samples."
    >
      <ResourceView resource={status} what="the service status">
        {(s) => {
          const o = overall(s);
          const tone = SERVICE_STATE[o.state].tone;
          const active = s.incidents.filter((i) => i.state !== 'Resolved');
          const past = s.incidents.filter((i) => i.state === 'Resolved');
          return (
            <>
              <header className={`st-overall p1-status--${tone}`}>
                <h1 className="st-headline">
                  <StatusMark tone={tone} size={32} />
                  {o.headline}
                </h1>
                <p>{o.detail}</p>
                <p className="st-checked p1-num">
                  Checked {formatTime(s.checkedAt, 'en', true)} · refreshes every minute
                </p>
              </header>

              {active.map((i) => (
                <Notice key={i.id} tone={SERVICE_STATE[i.impact].tone} title={`${i.state}: ${i.title}`}>
                  <p>{i.updates[0]?.text}</p>
                  <p className="p1-num">
                    Since {formatDateTime(i.startedAt, 'en')} · affects {i.components.join(', ')}
                  </p>
                </Notice>
              ))}

              <Section title="Components">
                <ul className="st-components" role="list">
                  {s.components.map((c) => (
                    <li key={c.key}>
                      <div className="st-component-head">
                        <div>
                          <p className="st-component-name">{c.name}</p>
                          <p className="p1x-muted st-component-what">{c.description}</p>
                        </div>
                        <ServiceStateLabel value={c.state} />
                      </div>
                      <History c={c} />
                    </li>
                  ))}
                </ul>
              </Section>

              <Section title="Scheduled maintenance">
                {s.maintenance.length === 0 ? (
                  <p className="p1x-muted">
                    {s.incidentHistoryAvailable
                      ? 'No maintenance is planned.'
                      : 'Planned maintenance will be announced here.'}
                  </p>
                ) : (
                  <ul className="st-list" role="list">
                    {s.maintenance.map((m) => (
                      <li key={m.id}>
                        <p className="st-component-name">{m.title}</p>
                        <p className="p1-num">
                          {formatDate(m.from, 'en', 'long')}, {formatTime(m.from, 'en')}–{formatTime(m.to, 'en')} ·{' '}
                          {m.components.join(', ')}
                        </p>
                        <p className="p1x-muted">{m.impact}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="Past incidents">
                {!s.incidentHistoryAvailable ? (
                  <p className="p1x-muted">
                    Incident history starts when this page is connected to P1Plug’s incident process. Until then, an
                    empty list here would not mean there were no incidents, so none is shown.
                  </p>
                ) : past.length === 0 ? (
                  <EmptyState positive title="No incidents in the last 90 days" />
                ) : (
                  past.map((i) => (
                    <article key={i.id} className="st-incident">
                      <h3 className="st-component-name">{i.title}</h3>
                      <p className="p1x-muted p1-num">
                        {formatDate(i.startedAt, 'en', 'long')} · {SERVICE_STATE[i.impact].label} ·{' '}
                        {i.components.join(', ')}
                        {i.resolvedAt
                          ? ` · resolved after ${Math.round((new Date(i.resolvedAt).getTime() - new Date(i.startedAt).getTime()) / 60_000)} minutes`
                          : ''}
                      </p>
                      <Timeline
                        now={now}
                        empty=""
                        events={i.updates.map((u) => ({
                          at: u.at,
                          kind: 'support' as const,
                          title: u.state,
                          detail: u.text,
                          tone: u.state === 'Resolved' ? ('success' as const) : undefined,
                        }))}
                      />
                    </article>
                  ))
                )}
              </Section>

              <footer className="st-footer p1x-muted">
                <p>
                  P1Plug delivers smart-meter data as a managed service. This page reports on the service, not on
                  individual devices.
                </p>
              </footer>
            </>
          );
        }}
      </ResourceView>
    </TopShell>
  );
}
